/**
 * Helper functions for responsive image calculations
 */

// Mobile breakpoint in pixels
export const MOBILE_MAX_WIDTH_PX = 735

// Available widths in our srcsets
export const MOBILE_SRCSET_WIDTHS = [240, 360, 480, 640, 735, 750]
export const DESKTOP_SRCSET_WIDTHS = [
  240, 480, 640, 720, 735, 960, 1200, 1680, 2160
]

/**
 * Parse a CSS size value and return the pixel width
 * @param sizeValue - CSS size value like "100vw", "50vw", "calc(100vw - 2rem)"
 * @param viewportWidth - The viewport width in pixels
 * @returns The calculated width in pixels
 */
export function parseSizeValue(
  sizeValue: string,
  viewportWidth: number
): number {
  // Handle simple vw values
  const vwMatch = sizeValue.match(/^(\d+(?:\.\d+)?)vw$/)
  if (vwMatch) {
    const vwValue = parseFloat(vwMatch[1])
    return (viewportWidth * vwValue) / 100
  }

  // Handle pixel values
  const pxMatch = sizeValue.match(/^(\d+(?:\.\d+)?)px$/)
  if (pxMatch) {
    return parseFloat(pxMatch[1])
  }

  // Handle calc expressions (simplified - only handles calc(Xvw - Yrem))
  const calcMatch = sizeValue.match(
    /calc\((\d+(?:\.\d+)?)vw\s*-\s*(\d+(?:\.\d+)?)rem\)/
  )
  if (calcMatch) {
    const vwValue = parseFloat(calcMatch[1])
    const remValue = parseFloat(calcMatch[2])
    // Assume 1rem = 16px
    return (viewportWidth * vwValue) / 100 - remValue * 16
  }

  // Default to viewport width if we can't parse
  console.warn(`Could not parse size value: ${sizeValue}`)
  return viewportWidth
}

/**
 * Find the optimal width from available srcset widths
 * @param targetWidth - The target width in pixels (including DPR)
 * @param availableWidths - Array of available widths from srcset
 * @returns The optimal width to use
 */
export function findOptimalWidth(
  targetWidth: number,
  availableWidths: number[]
): number {
  // Find the smallest width that's >= targetWidth
  const sortedWidths = [...availableWidths].sort((a, b) => a - b)

  for (const width of sortedWidths) {
    if (width >= targetWidth) {
      return width
    }
  }

  // If no width is large enough, return the largest available
  return sortedWidths[sortedWidths.length - 1]
}

/**
 * Calculate the optimal image width for a given size and viewport
 * @param sizeValue - CSS size value like "100vw", "50vw"
 * @param viewportWidth - The viewport width in pixels (default: 375 for mobile)
 * @param devicePixelRatio - The device pixel ratio (default: 3)
 * @param isMobile - Whether this is for mobile srcset
 * @returns The optimal width to request
 */
export function calculateOptimalImageWidth(
  sizeValue: string,
  viewportWidth: number = 375,
  devicePixelRatio: number = 3,
  isMobile: boolean = true
): number {
  // Parse the size value to get display width
  const displayWidth = parseSizeValue(sizeValue, viewportWidth)

  // Calculate actual pixel width needed (display width * DPR)
  const targetWidth = Math.ceil(displayWidth * devicePixelRatio)

  // Find optimal width from available srcset
  const availableWidths = isMobile
    ? MOBILE_SRCSET_WIDTHS
    : DESKTOP_SRCSET_WIDTHS

  const optimalWidth = findOptimalWidth(targetWidth, availableWidths)

  // For mobile images, cap at 750px to avoid oversized images
  if (isMobile && optimalWidth > 750) {
    return 750
  }

  return optimalWidth
}

/**
 * Get the format to use for preloading (matches what the browser will request)
 * Browser typically requests AVIF if supported, then WebP, then fallback
 */
export function getPreloadFormat(): "avif" | "webp" {
  // For now, assume AVIF is supported (most modern browsers)
  // In a real implementation, you might want to check user agent
  return "avif"
}

/**
 * Container sizes in pixels
 */
export const CONTAINER_SIZES = {
  "camp-container-mobile": 676,
  "camp-container": 1352,
  "camp-container-limit": 1728
}

/**
 * Calculate the exact displayed image size accounting for container constraints
 * @param sizeValue - CSS size value like "100vw", "50vw", "33.333vw"
 * @param viewportWidth - The viewport width in pixels
 * @param containerType - Optional container constraint type
 * @param padding - Optional padding in pixels (default: 16px for mobile, 0px for desktop)
 * @returns The exact display width in pixels
 */
export function calculateExactDisplaySize(
  sizeValue: string,
  viewportWidth: number,
  containerType?: keyof typeof CONTAINER_SIZES,
  padding: number = 0
): number {
  // Parse the base size from the CSS value
  const baseSize = parseSizeValue(sizeValue, viewportWidth)

  // Apply container constraints if specified
  let constrainedSize = baseSize
  if (containerType && CONTAINER_SIZES[containerType]) {
    const maxWidth = CONTAINER_SIZES[containerType] - padding * 2
    constrainedSize = Math.min(baseSize, maxWidth)
  }

  return Math.round(constrainedSize)
}

/**
 * Get responsive sizes attribute for more accurate image sizing
 * @param sizeMobile - Mobile size value (e.g., "50vw")
 * @param sizeDesktop - Desktop size value (e.g., "33.333vw")
 * @param containerType - Optional container constraint
 * @param fullWidth - Whether the image spans full width
 * @returns Sizes attribute string
 */
export function getResponsiveSizes(
  sizeMobile: string,
  sizeDesktop: string,
  containerType?: keyof typeof CONTAINER_SIZES,
  fullWidth: boolean = false
): string {
  const sizes: string[] = []

  // Mobile sizes with container constraints
  if (containerType && !fullWidth) {
    const mobileMax = CONTAINER_SIZES["camp-container-mobile"] - 32 // 16px padding each side
    sizes.push(
      `(max-width: ${MOBILE_MAX_WIDTH_PX}px) min(${sizeMobile}, ${mobileMax}px)`
    )

    // Desktop sizes with container constraints
    const desktopMax = CONTAINER_SIZES[containerType]
    sizes.push(`min(${sizeDesktop}, ${desktopMax}px)`)
  } else {
    // Full width or no container constraints
    sizes.push(`(max-width: ${MOBILE_MAX_WIDTH_PX}px) ${sizeMobile}`)
    sizes.push(sizeDesktop)
  }

  return sizes.join(", ")
}

/**
 * Calculate aspect ratio strings for responsive images
 * @param width - Desktop image width
 * @param height - Desktop image height
 * @param widthMobile - Optional mobile image width
 * @param heightMobile - Optional mobile image height
 * @param hasSeparateMobileImage - Whether separate mobile image exists
 * @returns Object with mobile and desktop aspect ratios
 */
export function calculateAspectRatios(
  width?: number | null,
  height?: number | null,
  widthMobile?: number | null,
  heightMobile?: number | null,
  hasSeparateMobileImage: boolean = false
): { mobile?: string; desktop?: string } {
  const mobileAspectRatio =
    hasSeparateMobileImage && widthMobile && heightMobile
      ? `${widthMobile} / ${heightMobile}`
      : width && height
        ? `${width} / ${height}`
        : undefined

  const desktopAspectRatio =
    width && height ? `${width} / ${height}` : undefined

  return {
    mobile: mobileAspectRatio,
    desktop: desktopAspectRatio
  }
}

/**
 * Parse combined sizes string into mobile and desktop values
 * @param sizes - Combined sizes string like "(max-width: 735px) 50vw, 33.333vw"
 * @param sizeMobile - Fallback mobile size
 * @param sizeDesktop - Fallback desktop size
 * @returns Object with mobile, desktop, and fallback sizes strings
 */
export function parseSizesAttribute(
  sizes: string | undefined,
  sizeMobile: string,
  sizeDesktop: string
): { mobile: string; desktop: string; fallback: string } {
  if (!sizes) {
    const fallback = `(max-width: ${MOBILE_MAX_WIDTH_PX}px) ${sizeMobile}, ${sizeDesktop}`
    return { mobile: sizeMobile, desktop: sizeDesktop, fallback }
  }

  // Parse the provided sizes string to extract mobile and desktop values
  const match = sizes.match(
    new RegExp(
      `\\(max-width:\\s*${MOBILE_MAX_WIDTH_PX}px\\)\\s*([^,]+),\\s*(.+)`
    )
  )

  if (match) {
    return {
      mobile: match[1].trim(),
      desktop: match[2].trim(),
      fallback: sizes
    }
  }

  // If no media query in sizes, use as-is for both
  return { mobile: sizes, desktop: sizes, fallback: sizes }
}
